import { fg } from '@atlaskit/platform-feature-flags';

import { getBscGlobalState } from '../../../common/utils/bsc-global-state/core';
import {
	type CategorizedStorageControlsCache,
	type CategorizedStorageControlsCacheLegacy,
	CategoryStorageType,
	PreferenceCategory,
} from '../../../types';
import { getPreferences } from '../../preferences-controls/get-preferences';

export const isThirdPartyAllowed = async (thirdParty: string): Promise<boolean> => {
	/*
    If no preferences for whatever reason, deny.
    If prefs exist and are default, they will default to block all.
    If third party has no category or data doesn't resolve, deny.
    If third party is StrictlyNecessary (gate on), allow unconditionally.
    If third party has a category, check preferences and allow/deny.
  */

	const { loadCache } = getBscGlobalState();

	const storageControlsData:
		| CategorizedStorageControlsCacheLegacy
		| CategorizedStorageControlsCache
		| undefined = await loadCache(CategoryStorageType.Cookies);

	let thirdPartyCategory: PreferenceCategory | null = null;

	if (storageControlsData) {
		const { thirdPartyKeys, categories } = storageControlsData;
		const thirdPartyCategoryNumber = thirdPartyKeys[thirdParty];
		const numToCategoryMap = {
			[categories.STRICTLY_NECESSARY]: PreferenceCategory.StrictlyNecessary,
			[categories.FUNCTIONAL]: PreferenceCategory.Functional,
			[categories.ANALYTICS]: PreferenceCategory.Analytics,
			[categories.MARKETING]: PreferenceCategory.Marketing,
			[categories.UNKNOWN]: PreferenceCategory.Unknown,
		};
		thirdPartyCategory =
			thirdPartyCategoryNumber !== null ? numToCategoryMap[thirdPartyCategoryNumber] : null;
	}

	// StrictlyNecessary items are always allowed regardless of stored preferences,
	// matching the behaviour of isProcessingAllowedByPreferences.
	if (
		thirdPartyCategory === PreferenceCategory.StrictlyNecessary &&
		fg('platform_moonjelly_bsc_sn_third_party_fix')
	) {
		return true;
	}

	// No preferences stored — deny all non-strictly-necessary third parties.
	const preferences = await getPreferences();
	if (!preferences) {
		return false;
	}

	// Check the user's stored preference for this category.
	let isAllowedByPreferences = false;
	if (thirdPartyCategory) {
		isAllowedByPreferences = preferences[thirdPartyCategory];
	}

	return isAllowedByPreferences;
};
